import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import { useRouteMatch } from 'react-router-dom';
import { useMeContext } from '~/modules/me/useMeContext';
import {
  DelaAcceptAllButton,
  DelaRejectAllButton,
  GoBackButton
} from '~/modules/common/components';
import { useDialogState } from '~/modules/common/hooks';
import { TextLoading } from '~/modules/common/components/DetailsPage';
import useGlobalNudgeActions from './hooks/useGlobalNudgeActions';
import EditResourceButton from './EditResourceButton';
import EditUserAvatarButton from './EditUserAvatarButton';
import AvatarUploadDialog from './AvatarUploadDialog';

const useStyles = makeStyles(theme => ({
  main: {
    display: 'flex',
    flexDirection: 'row',
    position: 'relative',
    color: theme.palette.primary.contrastText,
    alignItems: 'center',
    flexWrap: 'wrap',
    padding: theme.spacing(0, 2),
    [theme.breakpoints.down('sm')]: {
      height: 56
    }
  },
  header: {
    transition: `background-color ${theme.transition.timing} ease`,
    backgroundColor: theme.palette.primary.main
  },
  resourceTitle: {
    flexGrow: 1,
    marginLeft: 17.5,
    maxWidth: '60%',
    [theme.breakpoints.down('xs')]: {
      maxWidth: '35%'
    }
  },
  resourceTitleWithActions: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginLeft: 17.5,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    rowGap: theme.spacing(0.5),
    columnGap: theme.spacing(1.5)
  },
  back: {
    fontSize: theme.typography.h4.fontSize,
    fontWeight: theme.typography.fontWeightRegular,
    width: '1.5rem',
    height: '1.5rem'
  },
  editResourceButton: {
    position: 'absolute',
    right: theme.spacing(2),
    top: 5
  },
  delaRecommendationButtonsContainer: {
    display: 'flex',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap'
  }
}));

const useDelaButtonStyles = makeStyles(theme => ({
  root: {
    [theme.breakpoints.down('md')]: {
      fontSize: theme.spacing(1.3),
      padding: theme.spacing(0.3, 1)
    }
  }
}));

export const getGoBackPath = match => {
  switch (match.path) {
    case '/profile':
      return '/my-work';
    case '/resource-pool/:slug':
    default:
      return '/resource-pool';
  }
};

export const ResourceDetailsHeader = ({
  userDetails,
  classes: classesOverride,
  editable,
  toggleEditable,
  isResourceDetailsLoading,
  smallAvatar,
  showBackButton = true,
  canEditAvatar = true
}) => {
  const {
    featureFlags: {
      isPsaPrpWcagFixesQ4Enabled,
      isPsaPraaResourceMatchingEnabled
    }
  } = useMeContext();
  const classes = useStyles({ classes: classesOverride });
  const delaButtonClasses = useDelaButtonStyles();
  const match = useRouteMatch();
  const to = getGoBackPath(match);
  const { open, closeDialog, openDialog } = useDialogState(false);
  const goBackButtonClasses = useMemo(() => ({ back: classes.back }), [
    classes.back
  ]);

  const {
    hasAcceptCallbacks,
    hasRejectCallbacks,
    onAcceptAllNudges,
    onRejectAllNudges
  } = useGlobalNudgeActions();

  const nudgeButtonsVisible =
    isPsaPraaResourceMatchingEnabled &&
    (hasAcceptCallbacks || hasRejectCallbacks);

  return (
    <>
      {!isResourceDetailsLoading && showBackButton && (
        <GoBackButton
          dataQeId="ResourceBackButton"
          to={to}
          classes={goBackButtonClasses}
          contextual
        />
      )}
      <EditUserAvatarButton
        dataQeId="EditAvatarSection"
        editable={editable && canEditAvatar}
        user={userDetails}
        onEditClick={openDialog}
        isResourceDetailsLoading={isResourceDetailsLoading}
        small={smallAvatar}
      />
      <div
        className={
          isPsaPraaResourceMatchingEnabled
            ? classes.resourceTitleWithActions
            : classes.resourceTitle
        }
      >
        {isResourceDetailsLoading ? (
          <TextLoading />
        ) : (
          <>
            <Typography
              data-qe-id="UserNameLabel"
              variant="h6"
              noWrap
              {...(isPsaPrpWcagFixesQ4Enabled && {
                role: 'heading',
                'aria-level': 2
              })}
            >
              {userDetails.displayText}
            </Typography>
            {nudgeButtonsVisible && (
              <div className={classes.delaRecommendationButtonsContainer}>
                <DelaAcceptAllButton
                  classes={delaButtonClasses}
                  onClick={onAcceptAllNudges}
                  dataQeId="AcceptAllUserNudges"
                >
                  <FormattedMessage id="dela.acceptAll" />
                </DelaAcceptAllButton>
                <DelaRejectAllButton
                  classes={delaButtonClasses}
                  onClick={onRejectAllNudges}
                  dataQeId="RejectAllUserNudges"
                >
                  <FormattedMessage id="dela.rejectAll" />
                </DelaRejectAllButton>
              </div>
            )}
          </>
        )}
      </div>
      {!isPsaPraaResourceMatchingEnabled && (
        <div className={classes.editResourceButton}>
          <EditResourceButton
            dataQeId="ResourceEditSection"
            editable={editable}
            toggleEditable={toggleEditable}
          />
        </div>
      )}
      {!isResourceDetailsLoading && (
        <AvatarUploadDialog
          isOpen={open}
          onClose={closeDialog}
          userUri={userDetails.uri}
        />
      )}
    </>
  );
};

ResourceDetailsHeader.propTypes = {
  userDetails: PropTypes.object,
  classes: PropTypes.object,
  editable: PropTypes.bool.isRequired,
  toggleEditable: PropTypes.func,
  isResourceDetailsLoading: PropTypes.bool.isRequired,
  smallAvatar: PropTypes.bool,
  showBackButton: PropTypes.bool,
  canEditAvatar: PropTypes.bool
};

export default ResourceDetailsHeader;
